/**
 * Forecast panel (v1.3.0, Part 4): generates and displays the probabilistic
 * 1M/3M/6M/12M bear/base/bull scenarios for one company. Every number is a
 * documented estimate — the disclaimer is always rendered, never hidden.
 */

import React, { useCallback, useEffect, useState } from "react";
import type { AxiosError } from "axios";
import { apiClient } from "@/api/client";
import type { ForecastSet, PriceForecast } from "@/types";
import ConfidenceGauge from "@/components/research/ConfidenceGauge";
import ProbabilityBar from "@/components/research/ProbabilityBar";

function pct(value: string): string {
  const num = Number(value);
  return `${num >= 0 ? "+" : ""}${num.toFixed(1)}%`;
}

function money(value: string): string {
  return `$${Number(value).toFixed(2)}`;
}

function pctClass(value: string): string {
  return Number(value) >= 0 ? "text-signal-teal" : "text-signal-rose";
}

export default function ForecastPanel({ companyId }: { companyId: string }): React.ReactElement {
  const [forecastSet, setForecastSet] = useState<ForecastSet | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isGenerating, setIsGenerating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadLatest = useCallback(async () => {
    setIsLoading(true);
    try {
      const { data } = await apiClient.get<ForecastSet>(`/companies/${companyId}/forecast/latest`);
      setForecastSet(data);
    } catch {
      setForecastSet(null); // 404 simply means none generated yet
    } finally {
      setIsLoading(false);
    }
  }, [companyId]);

  useEffect(() => {
    void loadLatest();
  }, [loadLatest]);

  const handleGenerate = async (): Promise<void> => {
    setIsGenerating(true);
    setError(null);
    try {
      const { data } = await apiClient.post<ForecastSet>(`/companies/${companyId}/forecast`);
      setForecastSet(data);
    } catch (err) {
      const axiosError = err as AxiosError<{ detail: string }>;
      setError(axiosError.response?.data?.detail ?? "Unable to generate a forecast.");
    } finally {
      setIsGenerating(false);
    }
  };

  return (
    <div className="panel p-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="text-lg font-semibold text-mist-100">Probabilistic forecast</h2>
          {forecastSet && (
            <p className="mt-1 text-xs text-mist-400">
              Generated {new Date(forecastSet.generated_at).toLocaleString()} · reference price{" "}
              {money(forecastSet.reference_price)} · methodology {forecastSet.methodology_version}
            </p>
          )}
        </div>
        <button
          onClick={handleGenerate}
          disabled={isGenerating}
          className={`btn-primary ${isGenerating ? "sweep-bar" : ""}`}
        >
          {isGenerating ? "Computing…" : forecastSet ? "Refresh forecast" : "Generate forecast"}
        </button>
      </div>

      {error && <p className="mt-3 text-sm text-signal-rose">{error}</p>}

      {isLoading ? (
        <p className="mt-4 text-sm text-mist-400">Loading forecast…</p>
      ) : !forecastSet ? (
        <p className="mt-4 text-sm text-mist-400">
          No forecast yet. Generating one computes bear / base / bull scenarios for four horizons
          from this company's price history and its latest research scores.
        </p>
      ) : (
        <>
          <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4 stagger-children">
            {forecastSet.forecasts.map((horizon: PriceForecast) => (
              <div
                key={horizon.id}
                className="animate-rise rounded-lg border border-ink-700 bg-ink-800/50 p-4 transition-colors hover:border-ink-500"
              >
                <div className="flex items-start justify-between">
                  <p className="font-mono text-sm font-semibold text-mist-100">
                    {horizon.horizon_label}
                  </p>
                  <ConfidenceGauge
                    value={Number(horizon.confidence) * 100}
                    size={44}
                    label="conf."
                  />
                </div>

                {/* Probability distribution bar */}
                <div className="mt-3">
                  <ProbabilityBar
                    bearPct={Number(horizon.bear_return_pct)}
                    basePct={Number(horizon.base_return_pct)}
                    bullPct={Number(horizon.bull_return_pct)}
                    expectedPct={Number(horizon.expected_return_pct)}
                  />
                </div>

                <dl className="mt-3 space-y-1.5 text-sm">
                  <div className="flex justify-between">
                    <dt className="text-mist-400">Bear</dt>
                    <dd className="font-mono text-signal-rose">
                      {money(horizon.bear_price)} ({pct(horizon.bear_return_pct)})
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-mist-400">Base</dt>
                    <dd className="font-mono text-mist-100">
                      {money(horizon.base_price)} ({pct(horizon.base_return_pct)})
                    </dd>
                  </div>
                  <div className="flex justify-between">
                    <dt className="text-mist-400">Bull</dt>
                    <dd className="font-mono text-signal-teal">
                      {money(horizon.bull_price)} ({pct(horizon.bull_return_pct)})
                    </dd>
                  </div>
                  <div className="flex justify-between border-t border-ink-700 pt-1.5">
                    <dt className="text-mist-400">Expected</dt>
                    <dd className={`font-mono ${pctClass(horizon.expected_return_pct)}`}>
                      {money(horizon.expected_price)} ({pct(horizon.expected_return_pct)})
                    </dd>
                  </div>
                </dl>
                <p className="mt-2 text-xs text-mist-400">
                  Volatility {Number(horizon.volatility_annualized_pct).toFixed(1)}% ann.
                </p>
              </div>
            ))}
          </div>

          {forecastSet.forecasts.length > 0 && (
            <details className="mt-4 text-sm">
              <summary className="cursor-pointer text-mist-300 hover:text-mist-100">
                Key assumptions & primary risks
              </summary>
              <div className="mt-2 grid grid-cols-1 gap-4 sm:grid-cols-2">
                <ul className="space-y-1 text-mist-300">
                  {forecastSet.forecasts[0].assumptions.map((item, index) => (
                    <li key={index}>• {item}</li>
                  ))}
                </ul>
                <ul className="space-y-1 text-signal-amber/90">
                  {forecastSet.forecasts[0].risks.map((item, index) => (
                    <li key={index}>• {item}</li>
                  ))}
                </ul>
              </div>
            </details>
          )}

          <p className="mt-4 border-t border-ink-800 pt-3 text-xs leading-relaxed text-mist-400">
            {forecastSet.disclaimer}
          </p>
        </>
      )}
    </div>
  );
}
